/**
 * ShareModal — Radix Dialog for sharing a conversation session with
 * other Amazon employees by alias. V1: text input for alias (no Phonetool
 * autocomplete), permission selector (view/fork), optional message.
 *
 * The share endpoint (`POST /sessions/:id/share`) does not yet exist in the
 * generated SDK, so we call it via `wayneFetch` directly.
 */
import { useState, useCallback, type ReactElement } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import { Share2, X } from 'lucide-react';
import { wayneFetch } from '@/api/wayneFetch';

export type SharePermission = 'view' | 'fork' | 'edit';

export interface ShareModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly sessionId: string;
}

export default function ShareModal({
  open,
  onOpenChange,
  sessionId,
}: ShareModalProps): ReactElement {
  const [alias, setAlias] = useState('');
  const [permission, setPermission] = useState<SharePermission>('view');
  const [message, setMessage] = useState('');
  const [successAlias, setSuccessAlias] = useState('');

  const shareMutation = useMutation({
    mutationFn: (params: { sessionId: string; recipients: string[]; permission: string; message?: string }) =>
      wayneFetch(`/sessions/${params.sessionId}/share`, {
        method: 'POST',
        body: { recipients: params.recipients, permission: params.permission, message: params.message },
      }),
  });

  const status: 'idle' | 'submitting' | 'success' | 'error' = shareMutation.isPending
    ? 'submitting'
    : shareMutation.isSuccess
      ? 'success'
      : shareMutation.isError
        ? 'error'
        : 'idle';

  const resetForm = useCallback(() => {
    setAlias('');
    setPermission('view');
    setMessage('');
    setSuccessAlias('');
    shareMutation.reset();
  }, [shareMutation]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        resetForm();
      }
      onOpenChange(next);
    },
    [onOpenChange, resetForm],
  );

  const handleSubmit = useCallback(() => {
    if (!alias.trim()) return;
    const trimmedAlias = alias.trim();
    setSuccessAlias(trimmedAlias);
    const payload: { sessionId: string; recipients: string[]; permission: string; message?: string } = {
      sessionId,
      recipients: [trimmedAlias],
      permission,
    };
    if (message.trim()) {
      payload.message = message.trim();
    }
    shareMutation.mutate(
      payload,
      {
        onSuccess: () => {
          setTimeout(() => {
            handleOpenChange(false);
          }, 1500);
        },
      },
    );
  }, [alias, permission, message, sessionId, handleOpenChange, shareMutation]);

  const canSubmit = alias.trim().length > 0 && status !== 'submitting';

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md rounded-xl bg-wayne-surface border border-wayne-hairline p-6 shadow-xl focus:outline-none">
          {/* Close button */}
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute top-4 right-4 text-wayne-ink-3 hover:text-wayne-ink transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </Dialog.Close>

          {/* Header */}
          <div className="flex items-center gap-2 mb-5">
            <Share2 size={18} className="text-wayne-accent" aria-hidden="true" />
            <Dialog.Title asChild>
              <h3
                className="text-wayne-ink"
                style={{
                  fontFamily: 'var(--font-serif, Georgia, serif)',
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                }}
              >
                Share conversation
              </h3>
            </Dialog.Title>
          </div>

          {/* Alias input */}
          <label className="block mb-4">
            <span
              className="block text-wayne-ink-2 mb-1.5"
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              Add people
            </span>
            <input
              type="text"
              placeholder="Search by alias..."
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              disabled={status === 'submitting' || status === 'success'}
              className="w-full px-3 py-2 text-sm rounded-lg border border-wayne-hairline bg-wayne-bg text-wayne-ink placeholder:text-wayne-ink-3 focus:outline-none focus:border-wayne-accent transition-colors disabled:opacity-50"
            />
          </label>

          {/* Permission selector */}
          <fieldset className="mb-4">
            <legend
              className="text-wayne-ink-2 mb-2"
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              Permission
            </legend>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="share-permission"
                  value="view"
                  checked={permission === 'view'}
                  onChange={() => setPermission('view')}
                  disabled={status === 'submitting' || status === 'success'}
                  className="accent-wayne-accent"
                />
                <span className="text-wayne-ink" style={{ fontSize: 13 }}>
                  View
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="share-permission"
                  value="fork"
                  checked={permission === 'fork'}
                  onChange={() => setPermission('fork')}
                  disabled={status === 'submitting' || status === 'success'}
                  className="accent-wayne-accent"
                />
                <span className="text-wayne-ink" style={{ fontSize: 13 }}>
                  Fork
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="share-permission"
                  value="edit"
                  checked={permission === 'edit'}
                  onChange={() => setPermission('edit')}
                  disabled={status === 'submitting' || status === 'success'}
                  className="accent-wayne-accent"
                />
                <span className="text-wayne-ink" style={{ fontSize: 13 }}>
                  Edit
                </span>
              </label>
            </div>
          </fieldset>

          {/* Optional message */}
          <label className="block mb-5">
            <span
              className="block text-wayne-ink-2 mb-1.5"
              style={{ fontSize: 13, fontWeight: 500 }}
            >
              Note (optional)
            </span>
            <textarea
              rows={2}
              placeholder="Add a note (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={status === 'submitting' || status === 'success'}
              className="w-full px-3 py-2 text-sm rounded-lg border border-wayne-hairline bg-wayne-bg text-wayne-ink placeholder:text-wayne-ink-3 focus:outline-none focus:border-wayne-accent transition-colors resize-none disabled:opacity-50"
            />
          </label>

          {/* Status messages */}
          {status === 'success' && (
            <p className="text-sm text-wayne-accent mb-4" role="status">
              Shared with {successAlias}
            </p>
          )}
          {status === 'error' && (
            <p className="text-sm text-wayne-red mb-4" role="alert">
              Failed to share. Try again.
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Dialog.Close asChild>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-wayne-hairline text-wayne-ink hover:bg-wayne-surface-2 transition-colors"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2 text-sm rounded-lg font-medium bg-wayne-accent text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Sharing...' : 'Share'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
